import { Close } from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ExcelJS from 'exceljs';
import { formatDate } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getDailyBook } from '../../../../../../lib/api/accounting';
import GlobalSearcher from '../../../../../../lib/common/GlobalSearcher';
import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import { useAccountingPeriod } from '../../context/AccountingPeriodContext';
import FinancialStatementBook from '../FinancialStatementBook';
import MajorBook from '../MajorBook';
import { fetchWithDateChunking } from '../utils/fetchWithDateChunking';
import { mergeGroupedLinesToDateList } from '../utils/mergeStrategies';
import BookEntriesTable, { DayEntry, Entry, EntryLine } from './components';

dayjs.extend(utc);

const exportToExcel = async (
  entries: DayEntry[],
  startDate: Date,
  endDate: Date,
) => {
  // Mapeo de datos planos
  const plainData = entries.flatMap((day: DayEntry) => {
    return day.groupedLinesByCategoryAndBusiness.flatMap(
      (category: Entry, categoryIndex: number) => {
        return category.groupedLines.map(
          (line: EntryLine, lineIndex: number) => [
            categoryIndex + 1, // ID Categoría
            lineIndex + 1, // ID Línea
            line.entryId, // ID Asiento
            line.className, // Clase
            day.date, // Fecha
            category.businessIdentifier, // Rut cliente
            category.businessName, // Nombre cliente
            category.category, // Nombre asiento
            line.account, // Cuenta
            line.accountName, // Nombre de Cuenta
            line.description, // Descripción
            line.documentId, // Documento
            line.debit, // Débito
            line.credit, // Crédito
          ],
        );
      },
    );
  });

  // Encabezados de la hoja de Excel
  const headers = [
    'Asiento',
    'Línea',
    'ID Asiento',
    'Clase',
    'Fecha',
    'Rut cliente',
    'Nombre cliente',
    'Nombre asiento',
    'Cuenta',
    'Nombre de Cuenta',
    'Descripción',
    'Documento',
    'Debe',
    'Haber',
  ];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  // Agregar datos a la hoja
  [headers, ...plainData].forEach((rowData, rowIndex) => {
    const row = worksheet.addRow(rowData);

    if (rowIndex === 0) {
      // Formato para la primera fila (encabezados)
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getColumn(cell.col).width = 20; // Ajustar ancho de columna
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFDDDDDD' }, // Color gris claro
        };
      });
    }
  });

  // Guardar el archivo
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  // Descargar el archivo
  const a = document.createElement('a');
  a.href = url;
  a.download = `Libro Diario ${formatDate(startDate)} - ${formatDate(endDate)}.xlsx`;
  a.click();

  URL.revokeObjectURL(url);
};

interface BookEntriesProps {
  isTransactional?: boolean;
  allowEditing?: boolean;
  title?: string;
}

const BookEntries: React.FC<BookEntriesProps> = ({
  isTransactional = false,
  allowEditing = true,
  title = 'Libro de Entradas',
}) => {
  const { startDate, endDate, setPeriod } = useAccountingPeriod();
  const [filterStartDate, setFilterStartDate] = useState<string | null>(
    startDate,
  );
  const [filterEndDate, setFilterEndDate] = useState<string | null>(endDate);
  const [data, setData] = useState<DayEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [showMajorBook, setShowMajorBook] = useState(false);
  const [showFinancialStatementBook, setShowFinancialStatementBook] =
    useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
  }, []);

  const setUp = async (
    startDateParam: string,
    endDateParam: string,
    showLoading: boolean = true,
  ) => {
    setPeriod(startDateParam, endDateParam);

    try {
      if (showLoading) setLoading(true);
      const result = await fetchWithDateChunking({
        startDate: startDateParam,
        endDate: endDateParam,
        thresholdMonths: 6,
        apiFunction: getDailyBook,
        mergeStrategy: (responses) => {
          const allDateLists = responses.map(
            (response) => response?.groupedLinesToDateList || [],
          );
          return mergeGroupedLinesToDateList(allDateLists);
        },
        additionalParams: { transactional: isTransactional },
        dispatch,
      });

      setData(result);
    } catch (error) {
      console.error('Error fetching daily book:', error);
    }
    if (showLoading) setLoading(false);
  };

  const refreshData = async (showLoading: boolean = true) => {
    if (startDate && endDate) {
      await setUp(startDate, endDate, showLoading);
    }
  };

  const handleDataUpdate = (updatedEntries: DayEntry[]) => {
    setData(updatedEntries);
  };

  const handleDataRevert = (originalEntries: DayEntry[]) => {
    setData(originalEntries);
  };

  const hasLoadedInitial = React.useRef(false);
  useEffect(() => {
    if (startDate && endDate && !hasLoadedInitial.current) {
      hasLoadedInitial.current = true;
      void setUp(startDate, endDate);
    }
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <div>
        <HeaderWithActions
          title={`${title} - Período: ${startDate ? dayjs(startDate).format('DD/MM/YYYY') : ''} - ${endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}`}
          icon={<CalendarMonthIcon />}
          variant="body1"
          item={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}
              >
                {isTransactional && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 10,
                    }}
                  >
                    <CustomButton
                      color="secondary"
                      onClick={() => setShowMajorBook(true)}
                    >
                      Libro Mayor
                    </CustomButton>
                    <CustomButton
                      color="secondary"
                      onClick={() => setShowFinancialStatementBook(true)}
                    >
                      Balance
                    </CustomButton>
                  </div>
                )}
                <div>
                  <GlobalSearcher
                    filters={[
                      {
                        type: 'date',
                        label: 'Fecha de inicio',
                        dateKey: 'startDate',
                        value: filterStartDate,
                        setter: setFilterStartDate,
                      },
                      {
                        type: 'date',
                        label: 'Fecha de fin',
                        dateKey: 'endDate',
                        value: filterEndDate,
                        setter: setFilterEndDate,
                      },
                    ]}
                    triggerSearch={async () => {
                      if (filterStartDate && filterEndDate) {
                        await setUp(filterStartDate, filterEndDate);
                      }
                    }}
                    shortcuts={true}
                  />
                </div>
                <div>
                  <CustomButton
                    color="primary"
                    onClick={() => {
                      if (startDate && endDate) {
                        void exportToExcel(
                          data,
                          new Date(startDate),
                          new Date(endDate),
                        );
                      }
                    }}
                  >
                    Exportar
                  </CustomButton>
                </div>
              </div>
              {/* Dialog buttons */}
            </div>
          }
        />
      </div>
      <div>
        <BookEntriesTable
          entries={data}
          refreshData={refreshData}
          onDataUpdate={handleDataUpdate}
          onDataRevert={handleDataRevert}
          allowEditing={allowEditing}
          isTransactional={isTransactional}
        />
      </div>
      {showMajorBook && (
        <Dialog
          open={showMajorBook}
          onClose={() => setShowMajorBook(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            style: {
              height: '90vh',
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle>
            Libro Mayor Transaccional - Período:{' '}
            {startDate ? dayjs(startDate).format('DD/MM/YYYY') : ''} -{' '}
            {endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}
            <IconButton
              aria-label="close"
              onClick={() => setShowMajorBook(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <MajorBook
              transactional={true}
              startDate={startDate ? new Date(startDate) : undefined}
              endDate={endDate ? new Date(endDate) : undefined}
            />
          </DialogContent>
        </Dialog>
      )}
      {showFinancialStatementBook && (
        <Dialog
          open={showFinancialStatementBook}
          onClose={() => setShowFinancialStatementBook(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            style: {
              height: '90vh',
              maxHeight: '90vh',
            },
          }}
        >
          <DialogTitle>
            Balance Transaccional
            <IconButton
              aria-label="close"
              onClick={() => setShowFinancialStatementBook(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <FinancialStatementBook
              transactional={true}
              startDate={startDate ? new Date(startDate) : undefined}
              endDate={endDate ? new Date(endDate) : undefined}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default BookEntries;
